/**
 * Curated Collections Slider
 * Location: assets/curated-collections.js
 */

(function() {
  'use strict';

  // Initialize all sliders on the page
  function initSliders() {
    const sliders = document.querySelectorAll('[data-slider]');
    
    sliders.forEach(slider => {
      const prevBtn = slider.parentElement.querySelector('[data-slider-prev]');
      const nextBtn = slider.parentElement.querySelector('[data-slider-next]');
      
      if (!prevBtn || !nextBtn) return;

      // Get autoplay settings from data attributes
      const autoplayEnabled = slider.dataset.autoplay === 'true';
      const autoplayDelay = parseInt(slider.dataset.autoplayDelay) || 3000;
      
      let autoplayInterval = null;

      // Touch/Drag variables
      let isDragging = false;
      let startPos = 0;
      let currentTranslate = 0;
      let prevTranslate = 0;
      let animationID = 0;
      let currentIndex = 0;

      // Get slide width including gap
      function getSlideWidth() {
        const slide = slider.querySelector('.curated-collections__slide');
        if (!slide) return 0;
        
        const slideWidth = slide.offsetWidth;
        const gap = parseInt(window.getComputedStyle(slider).gap) || 20;
        
        return slideWidth + gap;
      }

      // Calculate scroll amount based on viewport
      function getScrollAmount() {
        const viewportWidth = window.innerWidth;
        const slideWidth = getSlideWidth();
        
        // Desktop/Laptop: scroll 2 items
        if (viewportWidth >= 1024) {
          return slideWidth * 2;
        }
        // Tablet/Mobile: scroll 1 item
        else {
          return slideWidth * 1;
        }
      }

      // Update button states
      function updateButtons() {
        const scrollLeft = slider.scrollLeft;
        const maxScroll = slider.scrollWidth - slider.clientWidth;
        
        prevBtn.disabled = scrollLeft <= 0;
        nextBtn.disabled = scrollLeft >= maxScroll - 1;
      }

      // Autoplay functions
      function startAutoplay() {
        if (!autoplayEnabled) return;
        
        stopAutoplay(); // Clear any existing interval
        
        autoplayInterval = setInterval(() => {
          const scrollLeft = slider.scrollLeft;
          const maxScroll = slider.scrollWidth - slider.clientWidth;
          
          // If at the end, scroll back to start
          if (scrollLeft >= maxScroll - 1) {
            slider.scrollTo({
              left: 0,
              behavior: 'smooth'
            });
          } else {
            // Scroll to next
            const scrollAmount = getScrollAmount();
            slider.scrollBy({
              left: scrollAmount,
              behavior: 'smooth'
            });
          }
        }, autoplayDelay);
      }

      function stopAutoplay() {
        if (autoplayInterval) {
          clearInterval(autoplayInterval);
          autoplayInterval = null;
        }
      }

      // Scroll to previous
      prevBtn.addEventListener('click', () => {
        const scrollAmount = getScrollAmount();
        slider.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth'
        });
        
        // Reset autoplay on manual interaction
        if (autoplayEnabled) {
          stopAutoplay();
          startAutoplay();
        }
      });

      // Scroll to next
      nextBtn.addEventListener('click', () => {
        const scrollAmount = getScrollAmount();
        slider.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        });
        
        // Reset autoplay on manual interaction
        if (autoplayEnabled) {
          stopAutoplay();
          startAutoplay();
        }
      });

      // Update buttons on scroll
      slider.addEventListener('scroll', updateButtons);

      // Update buttons on resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(updateButtons, 100);
      });

      // Initial button state
      updateButtons();

      // ===== TOUCH/DRAG FUNCTIONALITY =====
      
      // Disable default drag on images
      const images = slider.querySelectorAll('img');
      images.forEach(img => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });

      // Touch Start
      slider.addEventListener('touchstart', touchStart, { passive: true });
      slider.addEventListener('mousedown', touchStart);

      // Touch Move
      slider.addEventListener('touchmove', touchMove, { passive: false });
      slider.addEventListener('mousemove', touchMove);

      // Touch End
      slider.addEventListener('touchend', touchEnd);
      slider.addEventListener('mouseup', touchEnd);
      slider.addEventListener('mouseleave', touchEnd);

      function touchStart(event) {
        isDragging = true;
        startPos = getPositionX(event);
        prevTranslate = slider.scrollLeft;
        slider.style.cursor = 'grabbing';
        slider.style.scrollBehavior = 'auto';
        
        // Pause autoplay on drag
        stopAutoplay();
      }

      function touchMove(event) {
        if (!isDragging) return;
        
        const currentPosition = getPositionX(event);
        const diff = startPos - currentPosition;
        
        // Scroll the slider
        slider.scrollLeft = prevTranslate + diff;
        
        // Prevent default to avoid text selection while dragging
        if (event.type === 'mousemove') {
          event.preventDefault();
        }
      }

      function touchEnd() {
        if (!isDragging) return;
        
        isDragging = false;
        slider.style.cursor = 'grab';
        slider.style.scrollBehavior = 'smooth';
        
        // Update buttons after drag
        updateButtons();
        
        // Resume autoplay after drag
        if (autoplayEnabled) {
          startAutoplay();
        }
      }

      function getPositionX(event) {
        return event.type.includes('mouse') 
          ? event.pageX 
          : event.touches[0].clientX;
      }

      // Set initial cursor
      slider.style.cursor = 'grab';

      // Prevent click events when dragging
      let clickStartX = 0;
      let clickStartY = 0;
      
      slider.addEventListener('mousedown', (e) => {
        clickStartX = e.clientX;
        clickStartY = e.clientY;
      });

      slider.addEventListener('click', (e) => {
        const diffX = Math.abs(e.clientX - clickStartX);
        const diffY = Math.abs(e.clientY - clickStartY);
        
        // If moved more than 5px, prevent click (was a drag)
        if (diffX > 5 || diffY > 5) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      // Keyboard navigation
      slider.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          prevBtn.click();
          e.preventDefault();
        } else if (e.key === 'ArrowRight') {
          nextBtn.click();
          e.preventDefault();
        }
      });

      // Add tabindex for keyboard navigation
      slider.setAttribute('tabindex', '0');

      // Pause autoplay on hover
      slider.addEventListener('mouseenter', () => {
        if (autoplayEnabled) {
          stopAutoplay();
        }
      });

      // Resume autoplay on mouse leave
      slider.addEventListener('mouseleave', () => {
        if (autoplayEnabled && !isDragging) {
          startAutoplay();
        }
      });

      // Start autoplay if enabled
      if (autoplayEnabled) {
        startAutoplay();
      }

      // Clean up on section unload
      const section = slider.closest('[id^="curated-collections-"]');
      if (section) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.removedNodes.forEach((node) => {
              if (node === section || node.contains(section)) {
                stopAutoplay();
              }
            });
          });
        });
        
        observer.observe(document.body, { childList: true, subtree: true });
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSliders);
  } else {
    initSliders();
  }

  // Re-initialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', initSliders);
})();